import {useCallback, useEffect, useState} from 'react';
import urls from 'urls';
import {
  GlobalNavItemType,
  InvitationCategoryType,
  type MegaNavDataType,
  TabsEnum,
} from '~/common/components/MegaNav/MegaNavVariant/types';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {getLocalStorageContent, getNewContent} from '~library/utils/cache_utils';

export const useGetCategories = () => {
  const [megaNavData, setMegaNavData] = useState<MegaNavDataType | null>(
    // @ts-ignore
    getLocalStorageContent('meganav_content_test_meganav_tabs')
  );
  const [currentGlobalNav, setCurrentGlobalNav] = useState<TabsEnum>(TabsEnum.INVITATIONS);
  const [categories, setCategories] = useState(megaNavData?.globalNav?.[0]?.categories ?? []);
  const isLargishAndUp = useIsLargishAndUp();

  const fetchMegaNavContent = useCallback(async () => {
    const response = await getNewContent(
      `${urls.get('ajax_meganav_content')}?version=test_meganav_tabs`,
      'meganav_content_test_meganav_tabs',
      {
        method: 'get',
        credentials: 'same-origin',
      }
    );

    if (response) {
      // @ts-ignore
      setMegaNavData(response);
    }
  }, []);

  useEffect(() => {
    fetchMegaNavContent();
  }, [fetchMegaNavContent]);

  const getInfo = useCallback((data: GlobalNavItemType[]): [InvitationCategoryType[], TabsEnum] => {
    const {
      location: {pathname, search},
    } = window;

    const invitationsNav = data.find((nav) => nav.id === TabsEnum.INVITATIONS);
    const greetingCardsNav = data.find((nav) => nav.id === TabsEnum.CARDS);
    const signupSheetsNav = data.find((nav) => nav.id === TabsEnum.SUS);
    const eviteProNav = data.find((nav) => nav.id === TabsEnum.PRO);

    let category = invitationsNav?.categories ?? [];
    let globalNav = TabsEnum.INVITATIONS;

    // search galley page:
    if (pathname.includes('/search/')) {
      if (search.includes('filter=card')) {
        category = greetingCardsNav?.categories ?? [];
        globalNav = TabsEnum.CARDS;
      } else {
        category = invitationsNav?.categories ?? [];
        globalNav = TabsEnum.INVITATIONS;
      }

      return [category, globalNav];
    }

    // my events page
    if (pathname.includes('/my-events')) {
      if (search.includes('type=signup-sheets')) {
        category = signupSheetsNav?.categories ?? [];
        globalNav = TabsEnum.SUS;
      } else if (search.includes('type=card')) {
        category = greetingCardsNav?.categories ?? [];
        globalNav = TabsEnum.CARDS;
      } else {
        category = invitationsNav?.categories ?? [];
        globalNav = TabsEnum.INVITATIONS;
      }

      return [category, globalNav];
    }

    // cards page
    if (
      pathname.includes('/card/') ||
      pathname.includes('/cards/') ||
      pathname.includes('/egift-cards/') ||
      pathname.includes('/card-overview/')
    ) {
      category = greetingCardsNav?.categories ?? [];
      globalNav = TabsEnum.CARDS;
      return [category, globalNav];
    }

    // sus page
    if (pathname.includes('/signup-sheet/') || pathname.includes('/signup-sheets/')) {
      category = signupSheetsNav?.categories ?? [];
      globalNav = TabsEnum.SUS;
      return [category, globalNav];
    }

    // pro page
    if (pathname.endsWith('/pro') || pathname.endsWith('/pro/')) {
      category = eviteProNav?.categories ?? [];
      globalNav = TabsEnum.PRO;
      return [category, globalNav];
    }

    // default
    return [category, globalNav];
  }, []);

  useEffect(() => {
    if (megaNavData?.globalNav) {
      const [category, globalNav] = getInfo(megaNavData?.globalNav);

      let localCategories: InvitationCategoryType[] = category;

      if (isLargishAndUp) {
        localCategories = localCategories.filter((el) => !el.mobileOnly);
      }

      setCategories(localCategories);
      setCurrentGlobalNav(globalNav);
    }
  }, [getInfo, isLargishAndUp, megaNavData]);

  const getCategoriesForTab = useCallback(
    (tab: TabsEnum) => {
      if (!megaNavData?.globalNav) return [];

      const navItem = megaNavData.globalNav.find((nav) => nav.id === tab);
      return navItem?.categories ?? [];
    },
    [megaNavData]
  );

  return {
    currentGlobalNav,
    categories,
    megaNavData,
    getCategoriesForTab,
  };
};
